import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircleHelp } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'

/**
 * Header launcher for the customer-support feature. Mirrors NotificationBell's
 * placement/sizing in the header's right cluster. Opens a small popover with
 * two entry points rather than navigating directly, since "new ticket" and
 * "my tickets" are two genuinely different intents.
 */
export function SupportButton() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const goTo = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                aria-label="Get Support"
                className="
                  relative p-2 rounded-lg
                  text-gray-500 hover:text-gray-700 hover:bg-gray-100
                  dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800
                  transition-colors
                "
              >
                <CircleHelp className="h-5 w-5" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Get Support</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent align="end" className="w-56 p-1">
        <button
          type="button"
          onClick={() => goTo('/tenant/support/new')}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          New ticket
        </button>
        <button
          type="button"
          onClick={() => goTo('/tenant/support/tickets')}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          My tickets
        </button>
        {/* Build-surfaces Stage 0: the tenant-visible glossary lives here,
            the existing "help" entry point convention, rather than adding a
            new sidebar item (BUILD-SURFACES-SIMPLIFICATION-SEP22.md §5). */}
        <button
          type="button"
          onClick={() => goTo('/tenant/help/glossary')}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          Glossary
        </button>
      </PopoverContent>
    </Popover>
  )
}
